<script setup>
/**
 * One thing that is wrong, said in the owner's words, with the verb that fixes it.
 *
 * ONE ROW GRAMMAR, and every row keeps it: **[severity mark] [what · why] [count] [go]**.
 * The right-hand cluster is the same slot on every row, in the same order, so the eye
 * finds the action at the same place whatever the row says and whatever direction its
 * text runs in. THE PLAN, §4 P4 names the verbs: unanswered question → *Teach an
 * answer*; failed source → *Try again*; a channel with no bot → *Ask us to connect it*
 * (W-03 — it read *Connect it* and linked nowhere; see THE ONE ROW THAT ASKS below).
 *
 * THREE THINGS THIS FILE IS THE FIX FOR. All three were named by blind critics reading
 * this list against Chatwoot Captain without knowing whose work it was.
 *
 * 1. *"Four rows, three different icon colours … with no legend, so green appears in a
 *    list of problems and reads as 'this one is fine'."* Correct, and worse than it
 *    sounds: the accent our branding overlay renders emerald was sitting on the
 *    unanswered-question row. Colour is no longer a property of the KIND at all. It comes
 *    off `row.level`, which has two values and one meaning — see `ATTENTION_LEVEL` in
 *    `helpers/attention.js`, where the queue also sorts by it, so red can never appear
 *    below amber and the meaning is inferable from the list itself.
 *
 * 2. *"Row 1's 'Contact us to top up' is bare text with no button chrome while rows 2-4
 *    are grey pills, so the most urgent row is the one that looks least clickable."*
 *    Also correct, and unfixable HERE: that row was the balance, billing is manual, and
 *    there is no destination to send anyone to. Inventing a button that goes nowhere
 *    would have been the same class of lie as a fabricated zero. So the balance left the
 *    queue entirely (`CreditBalanceCard` owns it, alone, and already carries the "contact
 *    us" sentence next to the number). **Every row's action is still the same control**,
 *    and W-03 kept it that way while changing what one of them does: the
 *    disconnected-channel row is now a BUTTON that asks us rather than a link, and it
 *    renders from the same `ACTION_CLASS` as the links, so the pill is identical and only
 *    the tag differs. The fallback below stays all-or-nothing (a row mounted outside a
 *    matched route has no account id, so nothing links and nothing asks), never one row
 *    in four looking different from its neighbours.
 *
 * 3. *"'No assistant on فرع السالمية — واتساب' runs RTL text inline in an LTR heading,
 *    and the Arabic question row has 'Asked 6×' jammed against the end of the Arabic
 *    string rather than at a stable edge."* The title is now assembled from `isolateParts`
 *    so the name sits in a real `<bdi>` isolate, and the count moved out of the title line
 *    into the trailing cluster, where it sits at the page's inline-end on every row
 *    regardless of what direction the row's own text resolved to.
 *
 * The destinations that remain are routes that are already registered
 * (`wasloai.routes.js`); some still render `WasloNotReadyPage` until P5 and P8 land. That
 * is the pattern the route table sets out on purpose: the name, the path and the click
 * path are real now, and the component behind them is swapped in place. The one route
 * that was NOT real — `waslo/settings/channels`, which only redirects back here — is why
 * this file now has a button as well as a link.
 */
import { computed, getCurrentInstance, nextTick, ref } from 'vue';

import { frontendURL } from 'dashboard/helper/URLHelper';

import { useWasloI18n } from '../i18n.js';
import { wasloApi } from '../api/client.js';
import { ATTENTION_KIND, ATTENTION_LEVEL } from '../helpers/attention.js';
import { MARKER, isolateParts, wholeValue } from '../helpers/bidi.js';
import { toIntlLocale } from '../helpers/intl.js';
import { noteLangOf } from '../helpers/hand-to.js';
import { ensureNeedsAPersonLabel } from '../helpers/needs-a-person.js';

const props = defineProps({
  /** A row from `buildAttentionQueue`. */
  row: { type: Object, required: true },
  /** Chatwoot account id. Null (a page mounted outside a matched route) → no links. */
  accountId: { type: Number, default: null },
});

/**
 * `changed`: a row's fix changed something the page reads (P-1.3's "Send them to me").
 * `fix`: C3 P-6.4's "Fix it" on an unanswered row. The QUEUE opens the dialog, never this
 * row: a saved fix reloads the queue, this row leaves it, and the dialog still has to say
 * what the save did and offer "Check it now".
 */
const emit = defineEmits(['changed', 'fix']);

const { t, locale } = useWasloI18n();

/**
 * SEVERITY, and nothing else, decides colour. One entry per level, token pairs, never literals.
 * `n-ruby-*` is "already biting", `n-amber-*` is "about to". The accent (`n-blue-*`) is
 * deliberately absent: this is a list of problems, and the one colour on this page that
 * means "good" has no business in it.
 */
const LEVELS = {
  [ATTENTION_LEVEL.STOPPED]: {
    tile: 'bg-n-ruby-3',
    mark: 'text-n-ruby-11',
  },
  [ATTENTION_LEVEL.GAP]: {
    tile: 'bg-n-amber-3',
    mark: 'text-n-amber-11',
  },
  // Good news with a verb (P-4.6). Neutral grey: the accent stays out of this list, and green on
  // a to-do reads as "nothing to do".
  [ATTENTION_LEVEL.NEWS]: {
    tile: 'bg-n-slate-3',
    mark: 'text-n-slate-11',
  },
};

/**
 * The icon says WHAT, never how bad — that is the colour's one job. All three are lucide
 * outlines at the same weight so they read as one family rather than three moods.
 */
const KINDS = {
  [ATTENTION_KIND.DISCONNECTED_INBOX]: {
    icon: 'i-lucide-unplug',
    copy: 'DISCONNECTED_INBOX',
    // No `path`, and that is the fix, not an omission: see THE ONE ROW THAT ASKS below.
    path: null,
  },
  // P-5.9. The menu kinds open THAT menu (see `path` below); this is the fallback.
  [ATTENTION_KIND.MENU_TASK_MISSING]: {
    icon: 'i-lucide-list',
    copy: 'MENU_TASK_MISSING',
    path: 'waslo/menus',
  },
  [ATTENTION_KIND.FORM_OUT_OF_DATE]: {
    icon: 'i-lucide-clipboard-list',
    copy: 'FORM_OUT_OF_DATE',
    path: 'waslo/forms',
  },
  // G1: WhatsApp paused a form. Nothing to fix on her side; the Forms tab shows it.
  [ATTENTION_KIND.FORM_PAUSED]: {
    icon: 'i-lucide-circle-pause',
    copy: 'FORM_PAUSED',
    path: 'waslo/forms',
  },
  // P-1.3. The verb is a BUTTON ("Send them to me"); the path is the fallback to choose there.
  [ATTENTION_KIND.HANDOVER_UNSET]: {
    icon: 'i-lucide-user-round-plus',
    copy: 'HANDOVER_UNSET',
    path: 'waslo/personality',
  },
  [ATTENTION_KIND.FAILED_SOURCE]: {
    icon: 'i-lucide-file-warning',
    copy: 'FAILED_SOURCE',
    path: 'waslo/answers',
  },
  [ATTENTION_KIND.SOURCE_TAUGHT_NOTHING]: {
    icon: 'i-lucide-file-question',
    copy: 'SOURCE_TAUGHT_NOTHING',
    path: 'waslo/answers',
  },
  [ATTENTION_KIND.FAILING_CONNECTION]: {
    icon: 'i-lucide-plug',
    copy: 'FAILING_CONNECTION',
    // No fixed `path`: the verb opens THIS connection's editor (see `path` below).
    path: null,
  },
  [ATTENTION_KIND.TASK_CONNECTION_OFF]: {
    icon: 'i-lucide-plug-zap',
    copy: 'TASK_CONNECTION_OFF',
    path: 'waslo/settings/connections',
  },
  [ATTENTION_KIND.MENU_TASK_OFF]: {
    icon: 'i-lucide-power-off',
    copy: 'MENU_TASK_OFF',
    path: 'waslo/menus',
  },
  [ATTENTION_KIND.UNANSWERED]: {
    icon: 'i-lucide-message-circle-question',
    copy: 'UNANSWERED',
    path: 'waslo/answers',
  },
  // P-4.6. `path` is a function of the row here: each verdict opens ITS message, on ITS number.
  [ATTENTION_KIND.TEMPLATE_REFUSED]: {
    icon: 'i-lucide-file-x',
    copy: 'TEMPLATE_REFUSED',
    path: row =>
      row.inboxId === null
        ? 'waslo/templates'
        : `waslo/templates/${encodeURIComponent(row.templateId)}?inboxId=${row.inboxId}&name=${encodeURIComponent(row.name)}`,
  },
  [ATTENTION_KIND.TEMPLATE_PAUSED]: {
    icon: 'i-lucide-circle-pause',
    copy: 'TEMPLATE_PAUSED',
    path: row =>
      row.inboxId === null
        ? 'waslo/templates'
        : `waslo/templates?inboxId=${row.inboxId}`,
  },
  // P-1.7. Above the approval, in the same order as `ATTENTION_KIND`.
  [ATTENTION_KIND.TEAM_SUGGESTIONS]: {
    icon: 'i-lucide-lightbulb',
    copy: 'TEAM_SUGGESTIONS',
    path: 'waslo/answers',
  },
  // C3 P-6.9. The verb opens Answers, where the "For now" box is, to extend it.
  [ATTENTION_KIND.NOTICE_ENDING]: {
    icon: 'i-lucide-calendar-clock',
    copy: 'NOTICE_ENDING',
    path: 'waslo/answers',
  },
  [ATTENTION_KIND.TEMPLATE_APPROVED]: {
    icon: 'i-lucide-circle-check',
    copy: 'TEMPLATE_APPROVED',
    path: () => 'campaigns/broadcasts/new',
  },
};

/**
 * Every kind `buildAttentionQueue` can emit has an entry above, and `overview.spec.js`
 * mounts one row per `ATTENTION_KIND` to keep that true. So these are lookups, not
 * guards: a `v-if` on the root would have made a kind added to the helper without a
 * presentation here vanish silently from the queue — a row an owner never sees is worse
 * than a row that fails loudly in a spec.
 */
const kind = computed(() => KINDS[props.row.kind]);
const level = computed(() => LEVELS[props.row.level]);

const copyKey = computed(() => `WASLO.OVERVIEW.ATTENTION.${kind.value.copy}`);

const TEMPLATE_KINDS = [
  ATTENTION_KIND.TEMPLATE_REFUSED,
  ATTENTION_KIND.TEMPLATE_PAUSED,
  ATTENTION_KIND.TEMPLATE_APPROVED,
];

/** Meta's reason codes this build has words for; one literal key each, so a key search finds them. */
const TEMPLATE_REASON = {
  ABUSIVE_CONTENT: () =>
    t('WASLO.OVERVIEW.ATTENTION.TEMPLATE_REASON.ABUSIVE_CONTENT'),
  INCORRECT_CATEGORY: () =>
    t('WASLO.OVERVIEW.ATTENTION.TEMPLATE_REASON.INCORRECT_CATEGORY'),
  INVALID_FORMAT: () =>
    t('WASLO.OVERVIEW.ATTENTION.TEMPLATE_REASON.INVALID_FORMAT'),
  SCAM: () => t('WASLO.OVERVIEW.ATTENTION.TEMPLATE_REASON.SCAM'),
  TAG_CONTENT_MISMATCH: () =>
    t('WASLO.OVERVIEW.ATTENTION.TEMPLATE_REASON.TAG_CONTENT_MISMATCH'),
};

/** A code in her words; anything else exactly as Meta wrote it. `Object.hasOwn`, never `in`. */
const reasonSentence = reason => {
  const code = String(reason ?? '').trim();
  if (code === '') return '';
  return Object.hasOwn(TEMPLATE_REASON, code) ? TEMPLATE_REASON[code]() : code;
};

const languageName = code => {
  const lower = String(code ?? '').toLowerCase();
  if (lower.startsWith('ar')) return t('WASLO.OVERVIEW.ATTENTION.LANGUAGE.AR');
  if (lower.startsWith('en')) return t('WASLO.OVERVIEW.ATTENTION.LANGUAGE.EN');
  return String(code ?? '');
};

/**
 * The title is the ONLY place a row carries text the customer or the owner wrote — an
 * unanswered question, a channel name, a source title. It is returned as three parts, not
 * one interpolated string, so the template can put the written-by-someone-else part in a
 * `<bdi>`: `dir="auto"` alone gives the SENTENCE a base direction but still lets the
 * neutral characters touching an Arabic name (the em dash in `فرع السالمية — واتساب`, the
 * quotes around a source title) be swallowed by it and move.
 */
const titleParts = computed(() => {
  switch (props.row.kind) {
    case ATTENTION_KIND.UNANSWERED:
      // No frame at all: the row's title IS the customer's question, so `dir="auto"` on
      // the paragraph resolves the whole line to the language it was asked in.
      return wholeValue(props.row.query);
    // C3 P-6.9: the note is her own words, so it is the body, not the title.
    case ATTENTION_KIND.NOTICE_ENDING:
      return { before: t(`${copyKey.value}.TITLE`), value: '', after: '' };
    case ATTENTION_KIND.DISCONNECTED_INBOX:
      return isolateParts(
        t(`${copyKey.value}.TITLE`, { name: MARKER }),
        props.row.name
      );
    // Every row named by the owner's own words for a thing: a source, a menu, a task.
    case ATTENTION_KIND.FAILED_SOURCE:
    case ATTENTION_KIND.SOURCE_TAUGHT_NOTHING:
    case ATTENTION_KIND.MENU_TASK_MISSING:
    case ATTENTION_KIND.MENU_TASK_OFF:
    case ATTENTION_KIND.FORM_OUT_OF_DATE:
    case ATTENTION_KIND.FORM_PAUSED:
      return isolateParts(
        t(`${copyKey.value}.TITLE`, { title: MARKER }),
        props.row.title
      );
    case ATTENTION_KIND.FAILING_CONNECTION:
      return isolateParts(
        t(`${copyKey.value}.TITLE`, { name: MARKER }),
        props.row.name
      );
    // The task's title is the owner's own words, so it is the isolate; the connection's
    // name is a Latin identifier and sits in the sentence.
    case ATTENTION_KIND.TASK_CONNECTION_OFF:
      return isolateParts(
        t(`${copyKey.value}.TITLE`, {
          task: MARKER,
          connection: props.row.connection,
        }),
        props.row.title
      );
    // The message's name is the owner's own identifier, so it is the isolate.
    case ATTENTION_KIND.TEMPLATE_REFUSED:
    case ATTENTION_KIND.TEMPLATE_PAUSED:
    case ATTENTION_KIND.TEMPLATE_APPROVED:
      return isolateParts(
        t(`${copyKey.value}.TITLE`, {
          name: MARKER,
          language: languageName(props.row.language),
        }),
        props.row.name
      );
    // P-1.7: a count in the title, a plural choice (six forms in Arabic).
    case ATTENTION_KIND.TEAM_SUGGESTIONS: {
      const count = new Intl.NumberFormat(toIntlLocale(locale.value)).format(
        props.row.count
      );
      return {
        before: t(`${copyKey.value}.TITLE`, { count }, props.row.count),
        value: '',
        after: '',
      };
    }
    default:
      return { before: t(`${copyKey.value}.TITLE`), value: '', after: '' };
  }
});

// P-5.6: why a source could not be read, from its reason CODE, in her words. The ingester's
// own text never reaches this row any more (it named vendors and status numbers).
const body = computed(() => {
  if (props.row.kind === ATTENTION_KIND.NOTICE_ENDING) return props.row.text;
  if (props.row.kind === ATTENTION_KIND.FAILED_SOURCE) {
    return t(
      `WASLO.ANSWERS.REASON.${String(props.row.reason ?? 'unknown').toUpperCase()}`
    );
  }
  // P-3.5: "It failed 3 times this week.", a plural choice (six forms in Arabic).
  if (props.row.kind === ATTENTION_KIND.FAILING_CONNECTION) {
    const count = new Intl.NumberFormat(toIntlLocale(locale.value)).format(
      props.row.count
    );
    return t(`${copyKey.value}.BODY`, { count }, props.row.count);
  }
  // WhatsApp's own reason, in her words when it is a code we know.
  if (
    (props.row.kind === ATTENTION_KIND.TEMPLATE_REFUSED ||
      props.row.kind === ATTENTION_KIND.TEMPLATE_PAUSED) &&
    props.row.reason
  ) {
    return reasonSentence(props.row.reason);
  }
  return t(`${copyKey.value}.BODY`);
});

const verb = computed(() => t(`${copyKey.value}.VERB`));

/**
 * Where the verb goes: THAT connection, THAT menu, THAT source, THAT message (P-4.6, whose
 * kind `path` is a function of its row); else the kind's page.
 */
const path = computed(() => {
  const { row } = props;
  if (
    row.kind === ATTENTION_KIND.FAILING_CONNECTION &&
    typeof row.toolId === 'number'
  ) {
    return `waslo/settings/connections/${row.toolId}`;
  }
  if (
    (row.kind === ATTENTION_KIND.MENU_TASK_MISSING ||
      row.kind === ATTENTION_KIND.MENU_TASK_OFF) &&
    typeof row.menuId === 'number'
  ) {
    return `waslo/menus/${row.menuId}`;
  }
  if (
    row.kind === ATTENTION_KIND.SOURCE_TAUGHT_NOTHING &&
    typeof row.documentId === 'number'
  ) {
    return `waslo/answers/${row.documentId}`;
  }
  return typeof kind.value.path === 'function'
    ? kind.value.path(row)
    : kind.value.path;
});

const to = computed(() =>
  props.accountId === null || path.value === null
    ? null
    : frontendURL(`accounts/${props.accountId}/${path.value}`)
);

/**
 * Opening a verdict's verb marks it seen (P-4.6), so it leaves the queue. Fire and forget: she is
 * already on her way to the fix, and a mark that failed only means the row shows once more.
 */
const markSeen = () => {
  if (!TEMPLATE_KINDS.includes(props.row.kind)) return;
  if (props.accountId === null || typeof props.row.eventId !== 'number') return;
  wasloApi
    .markTemplateEventSeen(props.accountId, props.row.eventId)
    .catch(() => {});
};

/**
 * ───────────────────────────────────────────────────────────────────────────────────
 * THE ONE ROW THAT ASKS US INSTEAD OF SENDING HER SOMEWHERE.
 *
 * "No assistant on X" used to carry *Connect it*, a link to `waslo/settings/channels` —
 * a route whose only behaviour is a redirect back to this page (`wasloai.routes.js`). So
 * the most urgent row in the queue, the one that says nothing is being answered, was the
 * one control on the screen that did NOTHING when pressed. An owner clicks it, the page
 * flickers, the row is still there.
 *
 * It could not be fixed by pointing the link somewhere better, because there is nowhere
 * better: putting an assistant on a number is an operator action (one click on our side),
 * not something the workspace can do for itself. So the verb changed from a navigation to
 * a REQUEST, which is the true action, and the row reports what happened to it.
 *
 * FOUR STATES, and the difference between them is whether she should press again:
 *   idle    — the button, in the same chrome as every other row's action.
 *   pending — the same button, disabled, while the ask is in flight.
 *   sent    — no button at all: there is nothing left to press, and a live button over a
 *             request we already hold is how a person asks three times.
 *   error   — the button comes back, with a sentence saying it did not get through.
 *
 * The sentence lives in the row's TEXT column, beside the title and body, because that is
 * where this row's words are and a 45-character confirmation does not belong in a
 * fixed-width trailing cluster shared with three other rows.
 * ───────────────────────────────────────────────────────────────────────────────────
 */
const REQUEST_STATE = {
  IDLE: 'idle',
  PENDING: 'pending',
  SENT: 'sent',
  ERROR: 'error',
};

const requestState = ref(REQUEST_STATE.IDLE);

/**
 * Only this kind asks, and only when the page knows both which workspace it is in and
 * which channel the row is about. A row missing either is rendered as the plain,
 * unclickable fallback rather than as a button that cannot say what it is asking for.
 */
const canAsk = computed(
  () =>
    props.row.kind === ATTENTION_KIND.DISCONNECTED_INBOX &&
    props.accountId !== null &&
    typeof props.row.inboxId === 'number'
);

// ── P-1.3: "Send them to me" ───────────────────────────────────────────────────────
// The one-tap fix for "nobody gets passed-on chats": whoever is online, else the person
// pressing it, with the notes in her dashboard's language. Chatwoot's store gives her id;
// without it (a harness) the row falls back to the link into Personality.
const store = getCurrentInstance()?.proxy?.$store ?? null;
const myId = computed(() => {
  const id = store?.getters?.getCurrentUserID;
  return typeof id === 'number' ? id : null;
});
const canSendToMe = computed(
  () =>
    props.row.kind === ATTENTION_KIND.HANDOVER_UNSET &&
    props.accountId !== null &&
    myId.value !== null
);
const sendState = ref('idle');

const sendToMe = async () => {
  if (!canSendToMe.value || sendState.value === 'pending') return;
  sendState.value = 'pending';
  try {
    await wasloApi.updateConfig(props.accountId, {
      handover: {
        to: 'anyone',
        personId: myId.value,
        noteLang: noteLangOf(locale.value),
      },
    });
    await ensureNeedsAPersonLabel(store);
    sendState.value = 'idle';
    emit('changed');
  } catch {
    sendState.value = 'error';
  }
};

// ── C3 P-6.4: "Fix it" on an unanswered question asks the queue to open the fix dialog ──
const canFix = computed(
  () => props.row.kind === ATTENTION_KIND.UNANSWERED && props.accountId !== null
);

const isAsking = computed(() => requestState.value === REQUEST_STATE.PENDING);
const wasAsked = computed(() => requestState.value === REQUEST_STATE.SENT);
const requestFailed = computed(
  () => requestState.value === REQUEST_STATE.ERROR
);

const askLabel = computed(() =>
  isAsking.value ? t('WASLO.OVERVIEW.ATTENTION.REQUEST.SENDING') : verb.value
);

/** The confirmation line, so the focus that the vanished button held has somewhere to go. */
const sentLineRef = ref(null);

/**
 * `alreadyRequestedToday` is deliberately NOT branched on. It is our bookkeeping, and the
 * owner pressed a button once: telling her "you already asked" answers a question she did
 * not ask and reads as a refusal. Both answers mean the same thing to her — we have it.
 *
 * FOCUS SURVIVES THE PRESS, which took two decisions:
 *
 *   • `aria-disabled`, never the `disabled` attribute, while the ask is in flight. A
 *     disabled button is removed from the tab order AND blurred by the browser, so a
 *     keyboard user who pressed Enter would be dumped back at the top of the document
 *     mid-request. The guard on the first line of this function is what actually prevents
 *     the second send — it is synchronous, it runs before any re-render, and it is the
 *     rule `overview.spec.js` presses twice to prove. The attribute only ever ANNOUNCED
 *     that rule; letting it also enforce it cost the focus.
 *
 *   • On success the button is gone (there is nothing left to press), so focus is moved
 *     to the confirmation itself — a `tabindex="-1"` target that is programmatically
 *     focusable and not in the tab order. Without this the same drop happens one step
 *     later, and the `aria-live` region would announce a sentence the reader cannot then
 *     find. The live region stays: this moves the cursor, it does not do the announcing.
 */
const ask = async () => {
  if (!canAsk.value || isAsking.value || wasAsked.value) return;
  requestState.value = REQUEST_STATE.PENDING;
  try {
    await wasloApi.requestInboxConnect(props.accountId, props.row.inboxId);
    requestState.value = REQUEST_STATE.SENT;
  } catch {
    requestState.value = REQUEST_STATE.ERROR;
    return;
  }

  // OUTSIDE the try, and that is the whole point of the early return above. The ask has
  // already succeeded here; the only thing left is moving the cursor. Inside, an
  // `await nextTick()` that rejected — a render error anywhere in the tree flushed in the
  // same tick, nothing to do with this row — would land in that catch and repaint a
  // SUCCESSFUL ask as a failure, telling the owner to try again for a request we really
  // did send. Remote (`focus()` cannot throw and the optional chain covers a null ref),
  // but it is silent, it is wrong in the direction that makes her ask twice, and one
  // line removes the class rather than the instance.
  await nextTick();
  sentLineRef.value?.focus();
};

/**
 * The count is a fact about the ROW, not part of the question, so it lives in the
 * trailing cluster with the action. In the title line it was laid out immediately after
 * an RTL string and therefore landed in a different place on every row — the "jammed
 * against the end of the Arabic string" the critic saw. Here it is at the page's
 * inline-end, always, and it follows the page's direction rather than the question's.
 */
const count = computed(() =>
  props.row.kind === ATTENTION_KIND.UNANSWERED && props.row.count > 1
    ? t('WASLO.OVERVIEW.ATTENTION.ASKED', { count: props.row.count })
    : ''
);

/**
 * ONE ACTION TREATMENT, held in one string. The queue's promise is that the action sits
 * in the same place, in the same chrome, on every row — and a row whose action is a
 * button rather than a link must not be the exception that reintroduces the very defect
 * a blind critic named ("the most urgent row is the one that looks least clickable").
 * So the link and the button render from THIS, and `overview.spec.js` asserts there is
 * exactly one class string across the whole list.
 */
const ACTION_CLASS =
  'inline-flex items-center h-8 gap-1 px-3 text-sm font-medium rounded-lg text-n-slate-12 bg-n-alpha-2 hover:bg-n-slate-4';
</script>

<template>
  <li
    class="flex flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center"
    :data-waslo-attention-kind="row.kind"
    :data-waslo-attention-level="row.level"
  >
    <span
      class="grid rounded-lg shrink-0 place-items-center size-9"
      :class="level.tile"
      aria-hidden="true"
    >
      <span class="size-4" :class="[kind.icon, level.mark]" />
    </span>

    <!--
      `aria-live` on the COLUMN, not on the status line itself: the region has to be in
      the document before the thing it announces appears, and these two paragraphs are
      `v-if`s that are not. Nothing else in this column ever changes, so a polite region
      here announces exactly one thing — what became of the ask.
    -->
    <div class="flex flex-col min-w-0 gap-1" aria-live="polite">
      <p
        class="mb-0 text-sm font-medium break-words text-n-slate-12"
        dir="auto"
        data-waslo-attention-title
      >
        <!-- Three nodes, never one string: the middle one is a bidi ISOLATE. The spaces
             that belong to the sentence are INSIDE the interpolated parts, and Vue's
             `condense` drops the newline-bearing whitespace between these tags, so the
             line renders exactly as the translation wrote it. -->
        <span>{{ titleParts.before }}</span>
        <bdi v-if="titleParts.value" data-waslo-attention-title-value>{{
          titleParts.value
        }}</bdi>
        <span>{{ titleParts.after }}</span>
      </p>
      <p class="mb-0 text-sm break-words text-n-slate-11" dir="auto">
        {{ body }}
      </p>
      <!-- What happened to the ask, in the column where this row's words live. -->
      <p
        v-if="wasAsked"
        ref="sentLineRef"
        class="mb-0 text-sm break-words text-n-teal-11"
        tabindex="-1"
        data-waslo-attention-request-sent
      >
        {{ t('WASLO.OVERVIEW.ATTENTION.REQUEST.SENT') }}
      </p>
      <p
        v-else-if="requestFailed"
        class="mb-0 text-sm break-words text-n-ruby-11"
        data-waslo-attention-request-error
      >
        {{ t('WASLO.OVERVIEW.ATTENTION.REQUEST.FAILED') }}
      </p>
      <p
        v-else-if="sendState === 'error'"
        class="mb-0 text-sm break-words text-n-ruby-11"
        data-waslo-attention-send-error
      >
        {{ t('WASLO.OVERVIEW.ATTENTION.HANDOVER_UNSET.FAILED') }}
      </p>
    </div>

    <!-- The same slot, in the same order, on every row. -->
    <div class="flex items-center gap-3 shrink-0 sm:ms-auto ms-0">
      <span
        v-if="count"
        class="text-xs font-medium text-n-slate-11"
        data-waslo-attention-count
      >
        {{ count }}
      </span>
      <!-- A button, not a link, and only on the row whose fix is ours to make. It is gone
           once the ask is in: there is nothing left to press, and a live button over a
           request we already hold is how one channel gets asked about three times. -->
      <button
        v-if="canAsk && !wasAsked"
        type="button"
        :class="[ACTION_CLASS, isAsking ? 'opacity-60' : '']"
        :aria-disabled="isAsking"
        data-waslo-attention-verb
        @click="ask"
      >
        {{ askLabel }}
      </button>
      <button
        v-else-if="canSendToMe"
        type="button"
        :class="[ACTION_CLASS, sendState === 'pending' ? 'opacity-60' : '']"
        :aria-disabled="sendState === 'pending'"
        data-waslo-attention-verb
        @click="sendToMe"
      >
        {{ verb }}
      </button>
      <button
        v-else-if="canFix"
        type="button"
        :class="ACTION_CLASS"
        data-waslo-attention-verb
        @click="emit('fix', row)"
      >
        {{ verb }}
      </button>
      <router-link
        v-else-if="to"
        :to="to"
        :class="ACTION_CLASS"
        data-waslo-attention-verb
        @click="markSeen"
      >
        {{ verb }}
      </router-link>
      <span
        v-else-if="!canAsk"
        class="inline-flex items-center h-8 px-3 text-sm font-medium text-n-slate-11"
        data-waslo-attention-verb
      >
        {{ verb }}
      </span>
    </div>
  </li>
</template>
